/**
 * AnnotationLayer
 * Überlagert den Potree-Canvas mit allen Annotation-Markern.
 * Reagiert auf Klick im "place"- und "relocate"-Modus.
 *
 * Semantic Zoom: Zeigt Marker als Cluster in der Übersichts-Stufe,
 * als Punkte im Kontext, und mit Labels + Popups im Detail/Inspect.
 *
 * PICKING-DELEGATION: Das Picking wird vollständig in script.js durchgeführt.
 * script.js fängt mousedown ab, macht natives Potree-Picking und sendet:
 *  - potree:createAnnotation  (place-Modus)
 *  - potree:relocateAnnotation (relocate-Modus)
 */

import React, { useEffect, useRef, useMemo } from 'react'
import { useAnnotationStore } from '../../store/annotations'
import { useAnnotationMarkers } from '../../hooks/useAnnotationMarkers'
import { useAnnotationClusters } from '../../hooks/useAnnotationClusters'
import { captureAndStoreAnnotationThumbnail } from '../../lib/thumbnailService'
import { AnnotationMarker } from './AnnotationMarker'
import { ClusterMarker } from './ClusterMarker'
import { Vec3, AnnotationCamera } from '../../types/annotations'
import type { ZoomTier, AnnotationConfig, AnnotationPopupConfig } from '../../hooks/useSemanticZoom'

interface AnnotationLayerProps {
  viewerRef: React.RefObject<any>
  currentCloudId: string | null
  /** Current semantic zoom tier */
  tier?: ZoomTier
  /** Annotation layer config from semantic zoom */
  tierConfig?: AnnotationConfig
  /** Popup config from semantic zoom */
  popupConfig?: AnnotationPopupConfig
  /** Show markers only when Notes section is active */
  visible?: boolean
}

export function AnnotationLayer({
  viewerRef,
  currentCloudId,
  tier = 'detail',
  tierConfig,
  popupConfig,
  visible = true,
}: AnnotationLayerProps) {
  const annotations = useAnnotationStore(s => s.annotations)
  const editorMode = useAnnotationStore(s => s.editor.mode)
  const activeAnnotationId = useAnnotationStore(s => s.editor.activeAnnotationId)
  const createAnnotation = useAnnotationStore(s => s.createAnnotation)
  const updateAnnotation = useAnnotationStore(s => s.updateAnnotation)
  const openAnnotationEditor = useAnnotationStore(s => s.openAnnotationEditor)
  const closeEditor = useAnnotationStore(s => s.closeEditor)
  const filterType = useAnnotationStore(s => s.filterType)
  const searchQuery = useAnnotationStore(s => s.searchQuery)
  const selectedAnnotationId = useAnnotationStore(s => s.selectedAnnotationId)

  const markersContainerRef = useRef<HTMLDivElement | null>(null)

  // Gefilterte Annotationen (gleiche Logik wie in AnnotationList)
  const filteredAnnotations = useMemo(() => {
    let result = annotations.filter(a => !currentCloudId || a.cloudId === currentCloudId)
    if (filterType !== 'all') result = result.filter(a => a.type === filterType)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        a =>
          a.title.toLowerCase().includes(q) ||
          a.tooltip.toLowerCase().includes(q) ||
          a.tags.some(t => t.toLowerCase().includes(q))
      )
    }
    return result
  }, [annotations, currentCloudId, filterType, searchQuery])

  // Set of visible annotation IDs for quick lookup
  const visibleIds = useMemo(
    () => new Set(filteredAnnotations.map(a => a.id)),
    [filteredAnnotations]
  )

  // ─── Semantic Zoom: Annotation Clustering ────────────────────────────────
  const { clusters, clusteredIds } = useAnnotationClusters({
    annotations: filteredAnnotations,
    viewerRef,
    tier,
    enabled: tier === 'overview',
  })

  useAnnotationMarkers({
    annotations,
    viewerRef,
    currentCloudId,
    containerRef: markersContainerRef,
  })

  // ─── Place: neue Annotation erstellen ─────────────────────────────────────
  useEffect(() => {
    function handleCreateAnnotation(e: Event) {
      const detail = (e as CustomEvent).detail as {
        worldPoint: Vec3
        camera: AnnotationCamera
        cloudId: string
      }
      if (!detail) return

      const annotation = createAnnotation({
        title: 'Neue Notiz',
        tooltip: '',
        type: 'position_marker',
        position: detail.worldPoint,
        camera: detail.camera,
        cloudId: detail.cloudId,
        tags: [],
      })
      // Thumbnail capturen — Viewer zeigt gerade die richtige Position
      try { captureAndStoreAnnotationThumbnail(annotation.id) } catch { /* noop */ }
      openAnnotationEditor(annotation.id)
    }

    window.addEventListener('potree:createAnnotation', handleCreateAnnotation)
    return () => window.removeEventListener('potree:createAnnotation', handleCreateAnnotation)
  }, [createAnnotation, openAnnotationEditor])

  // ─── Relocate: bestehende Annotation verschieben ──────────────────────────
  useEffect(() => {
    function handleRelocate(e: Event) {
      const detail = (e as CustomEvent).detail as {
        worldPoint: Vec3
        camera: AnnotationCamera
      }
      if (!detail) return

      const id = useAnnotationStore.getState().editor.activeAnnotationId
      if (!id) return

      updateAnnotation(id, {
        position: detail.worldPoint,
        camera: detail.camera,
      })
      openAnnotationEditor(id)
    }

    window.addEventListener('potree:relocateAnnotation', handleRelocate)
    return () => window.removeEventListener('potree:relocateAnnotation', handleRelocate)
  }, [updateAnnotation, openAnnotationEditor])

  // ─── Place/Relocate-Modus: script.js steuern ─────────────────────────────
  useEffect(() => {
    if (editorMode === 'place') {
      window.dispatchEvent(new CustomEvent('potree:startPlaceMode'))
    } else if (editorMode === 'relocate') {
      window.dispatchEvent(new CustomEvent('potree:startRelocateMode'))
    } else {
      window.dispatchEvent(new CustomEvent('potree:stopPlaceMode'))
    }
    return () => {
      window.dispatchEvent(new CustomEvent('potree:stopPlaceMode'))
    }
  }, [editorMode])

  const isPickingMode = editorMode === 'place' || editorMode === 'relocate'

  // Marker nur anzeigen wenn Notes-Section aktiv ist (oder im Place/Relocate-Modus)
  const showMarkers = visible || isPickingMode

  return (
    <div
      data-component="AnnotationLayer"
      data-mode={editorMode}
      data-tier={tier}
      className="annotation-layer"
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        /* Muss exakt über dem Potree-Canvas liegen (gleicher Offset) */
        left: 'var(--shell-offset, 0px)',
        right: 0,
        zIndex: 10050,
        pointerEvents: 'none',
        display: showMarkers ? undefined : 'none',
      }}
    >
      {/* Modus-Hinweis */}
      {isPickingMode && (
        <div
          data-section="placement-hint"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'hsl(240 6% 10% / 0.9)',
            border: `1px solid ${editorMode === 'relocate' ? 'hsl(25 100% 55% / 0.5)' : 'hsl(221 83% 63% / 0.5)'}`,
            borderRadius: '0.5em',            /* 8px at 16px */
            padding: '0.625em 1.125em',       /* 10px/16px, 18px/16px */
            fontSize: '0.75em',               /* 12px at 16px */
            fontWeight: 500,
            color: 'hsl(0 0% 92%)',
            pointerEvents: 'none',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            fontFamily: 'var(--font-sans, system-ui)',
          }}
        >
          {editorMode === 'place'
            ? 'Klicke auf einen Punkt in der Wolke, um eine Notiz zu platzieren'
            : 'Klicke auf einen neuen Punkt, um die Notiz zu verschieben'}
        </div>
      )}

      {/* Abbrechen-Button bei Relocate */}
      {editorMode === 'relocate' && (
        <button
          data-element="btn-cancel-relocate"
          onClick={() => {
            if (activeAnnotationId) openAnnotationEditor(activeAnnotationId)
            else closeEditor()
          }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, calc(-50% + 2.5em))', /* 40px at 16px */
            pointerEvents: 'auto',
            padding: '0.375em 1em',     /* 6px/16px, 16px/16px */
            borderRadius: '0.375em',    /* 6px at 16px */
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            fontSize: '0.6875em',       /* 11px at 16px */
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-sans, system-ui)',
            backdropFilter: 'blur(8px)',
          }}
        >
          Abbrechen (Esc)
        </button>
      )}

      {/* ── Cluster-Marker (nur in Overview) ─────────────────────── */}
      {tier === 'overview' && clusters.map(cluster => (
        <div
          key={cluster.id}
          data-cluster-id={cluster.id}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            transform: `translate3d(${cluster.centerScreen.x - 18}px, ${cluster.centerScreen.y - 18}px, 0)`,
            pointerEvents: 'auto',
            zIndex: 10060,
          }}
        >
          <ClusterMarker cluster={cluster} viewerRef={viewerRef} />
        </div>
      ))}

      {/* Marker-Container */}
      <div
        ref={markersContainerRef}
        data-section="markers-layer"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        {annotations
          .filter(a => !currentCloudId || a.cloudId === currentCloudId)
          .map(annotation => {
            const isVisible = visibleIds.has(annotation.id)
            const isSelected = selectedAnnotationId === annotation.id
            // Im Overview-Modus: geclusterte Annotationen ausblenden
            const isClustered = tier === 'overview' && clusteredIds.has(annotation.id)
            return (
              <div
                key={annotation.id}
                data-annotation-id={annotation.id}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  visibility: 'hidden',
                  pointerEvents: isPickingMode || !isVisible || isClustered ? 'none' : 'all',
                  willChange: 'transform, opacity',
                  transition: 'opacity 0.3s ease',
                  display: isVisible && !isClustered ? undefined : 'none',
                  zIndex: isSelected ? 100 : 1,
                }}
              >
                <AnnotationMarker
                  annotation={annotation}
                  viewerRef={viewerRef}
                  tier={tier}
                  tierConfig={tierConfig}
                  popupConfig={popupConfig}
                />
              </div>
            )
          })}
      </div>
    </div>
  )
}
